import { useEffect, useRef, useState } from "react";
import { Menu, X, Instagram } from "lucide-react";
import { BOOKING_URL, LOGO_URL, SOCIALS } from "../lib/data";

const links = [
  { href: "#about", label: "About" },
  { href: "#services", label: "Services" },
  { href: "#why", label: "Why Us" },
  { href: "#gallery", label: "Gallery" },
  { href: "#enquire", label: "Waitlist" },
  { href: "#faq", label: "FAQ" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      ref={ref}
      data-testid="site-nav"
      className={`sticky top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#F9F6F0]/85 backdrop-blur-xl border-b border-[#E6DEC3]/60 py-3"
          : "bg-cream py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-3"
        >
          <img
            src={LOGO_URL}
            alt="Barks & Co logo"
            className="h-12 w-12 rounded-full object-cover ring-1 ring-[#E6DEC3]"
          />
          <div className="leading-tight hidden sm:block">
            <div className="font-serif text-xl text-brown-dark tracking-wide">
              Barks &amp; Co
            </div>
            <div className="eyebrow" style={{ fontSize: "0.6rem" }}>
              Boutique Dog Grooming
            </div>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
              className="text-sm font-medium text-brown-dark/90 hover:text-lilac-dark transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-lilac-dark group-hover:w-full transition-all duration-300" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={SOCIALS.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-instagram-button"
            aria-label="Follow us on Instagram"
            className="hidden sm:inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-[#E6DEC3] bg-cream text-brown-dark hover:bg-lilac-soft hover:border-lilac transition-colors text-[11px] font-medium"
          >
            <Instagram size={13} className="text-lilac-dark" />
            Follow
          </a>
          <a
            href={BOOKING_URL}
            data-testid="nav-book-button"
            className="btn-primary !py-2.5 !px-5 text-sm hidden sm:inline-flex"
          >
            Join Waitlist
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((s) => !s)}
            className="md:hidden p-2 rounded-full bg-cream-alt text-brown-dark"
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="md:hidden bg-[#F9F6F0]/98 backdrop-blur-xl border-t border-[#E6DEC3]/60"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-brown-dark text-lg font-serif"
                data-testid={`nav-mobile-link-${l.label
                  .toLowerCase()
                  .replace(" ", "-")}`}
              >
                {l.label}
              </a>
            ))}
            <a
              href={BOOKING_URL}
              data-testid="nav-mobile-book-button"
              className="btn-primary mt-2 self-start"
            >
              Join Waitlist
            </a>
            <a
              href={SOCIALS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-mobile-instagram"
              className="inline-flex items-center gap-2 text-brown-dark text-sm mt-1 self-start"
            >
              <Instagram size={16} className="text-lilac-dark" />
              Follow us on Instagram
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
